import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, Grid,
  Alert, CircularProgress, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Stack, Chip, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const COLORS = ['#1E3A5F','#2E7D32','#1565C0','#7B1FA2','#C62828','#E65100','#00838F','#4527A0','#AD1457','#558B2F'];

export default function CategoriesPage() {
  const { user } = useAuth();
  const isAdmin = ['admin', 'support_team'].includes(user?.role);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ name: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    axios.get(`${API_URL}/categories`, { headers })
      .then(r => setCategories(r.data))
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };

  const openAdd = () => { setEditCat(null); setForm({ name: '' }); setOpenForm(true); };
  const openEdit = (c) => { setEditCat(c); setForm({ name: c.name }); setOpenForm(true); };

  const handleSubmit = async () => {
    setError('');
    try {
      if (editCat) {
        await axios.put(`${API_URL}/categories/${editCat.id}`, form, { headers });
        setSuccess('Category updated');
      } else {
        await axios.post(`${API_URL}/categories`, form, { headers });
        setSuccess('Category added');
      }
      setOpenForm(false); setEditCat(null); setForm({ name: '' }); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${API_URL}/categories/${id}`, { headers });
      setSuccess('Category deleted'); load();
    } catch { setError('Failed to delete'); }
  };

  const filtered = categories.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.5px' }}>Categories</Typography>
          <Typography color="text.secondary" variant="body2">{categories.length} project categories</Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            sx={{ borderRadius: 2, fontWeight: 700, boxShadow: '0 4px 14px rgba(30,58,95,0.25)' }}>
            Add Category
          </Button>
        )}
      </Box>

      <Card sx={{ mb: 3, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <TextField fullWidth size="small" placeholder="Search categories..."
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }} />
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((c, i) => {
            const color = COLORS[i % COLORS.length];
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={c.id}>
                <Card sx={{ border: `1px solid ${color}30`, boxShadow: 'none', borderRadius: 3,
                  transition: 'all 0.15s', '&:hover': { borderColor: color, transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${color}15` } }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: color + '18',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
                        <CategoryIcon fontSize="small" />
                      </Box>
                      {isAdmin && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(c)}
                              sx={{ width: 28, height: 28, bgcolor: 'action.hover' }}>
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}
                              sx={{ width: 28, height: 28, bgcolor: '#FEE2E2' }}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color }}>{c.name}</Typography>
                    <Typography variant="caption" color="text.disabled">ID #{c.id}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {filtered.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
                <CategoryIcon sx={{ fontSize: 48, opacity: 0.12, display: 'block', mx: 'auto', mb: 1 }} />
                <Typography color="text.secondary">No categories found</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1E3A5F, #2E75B6)', borderRadius: '12px 12px 0 0' }} />
        <DialogTitle sx={{ fontWeight: 800, pt: 3 }}>{editCat ? 'Edit Category' : 'Add New Category'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField fullWidth required label="Category Name" size="small" value={form.name}
            onChange={e => setForm({ name: e.target.value })} sx={{ mt: 1 }}
            onKeyPress={e => e.key === 'Enter' && handleSubmit()} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setOpenForm(false)} sx={{ flex: 1, borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!form.name.trim()}
            sx={{ flex: 2, borderRadius: 2, fontWeight: 700 }}>
            {editCat ? 'Save Changes' : 'Add Category'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
